import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Badge,
  ProgressBar,
  Button,
  Row,
  Col,
} from "react-bootstrap";
import moment from "moment";
import "moment/locale/es";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./style.css";

// Components
import { Spinner, Alert } from "../../../Utilidades";

// Actions de Redux
import { obtenerRutasAction } from "../../../../redux/actions/rutasActions";
import {
  editarRutaAction,
  resetEditarRutaAction,
} from "../../../../redux/actions/rutasActions";

const ListaRutas = () => {
  const dispatch = useDispatch();
  const [rutasLocal, setRutasLocal] = useState([]);
  const [rutaSeleccionada, setRutaSeleccionada] = useState();

  const { user } = useSelector((state) => state.login);

  const {
    rutas,
    loading,
    loadingUpdate,
    error,
    errorMessage,
    data: dataUpdateRuta,
  } = useSelector((state) => state.rutas);

  useEffect(() => {
    setRutasLocal(rutas);
  }, [rutas]);

  const [filtro] = useState("1");
  const rutasContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (filtro && user && user?.usuario?.uid) {
      dispatch(obtenerRutasAction(filtro, user.usuario.uid));
      if (rutasContainerRef.current) {
        rutasContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [filtro, user, dispatch]);

  const onAccionClick = (ruta, accion) => {
    // 🚨 Verificar si hay alguna ruta iniciada pero no finalizada
    const hayRutaActiva = rutasLocal.some(
      (r) => r.started && !r.ended && r.id !== ruta.id // Excluir la ruta actual
    );

    if (accion === "iniciar" && hayRutaActiva) {
      // Mostrar alerta y bloquear
      Swal.fire({
        title: "No se puede iniciar",
        text: "Ya existe una ruta iniciada que no ha sido finalizada. Por favor finalice la ruta actual antes de iniciar una nueva.",
        icon: "warning",
        confirmButtonText: "Entendido",
      });
      return; // Salir de la función
    }

    return Swal.fire({
      title: `¿Está seguro que desea ${accion} la ruta?`,
      text: "",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        let data = {};
        if (accion === "iniciar") {
          data = {
            id: ruta.id,
            started: true,
            usuarios_id_started: user.usuario.uid,
          };
        } else if (accion === "finalizar") {
          data = {
            id: ruta.id,
            ended: true,
            usuarios_id_ended: user.usuario.uid,
          };
        }
        setRutaSeleccionada(ruta);
        dispatch(editarRutaAction(data));
      }
    });
  };

  const onRedirectToPedidos = (ruta) => {
    if (!loadingUpdate) {
      if (ruta.started) {
        navigate(`/ruta/${ruta.id}`);
      } else {
        onAccionClick(ruta, "iniciar");
      }
    }
  };

  useEffect(() => {
    if (
      !loadingUpdate &&
      !error &&
      dataUpdateRuta &&
      dataUpdateRuta?.resultado
    ) {
      dispatch(resetEditarRutaAction());

      const { started, started_at, ended, ended_at } = dataUpdateRuta.ruta;

      setRutasLocal((prev) =>
        prev.map((p) =>
          p.id === rutaSeleccionada?.id
            ? {
                ...p,
                started,
                started_at,
                ended,
                ended_at,
              }
            : p
        )
      );

      Swal.fire({
        icon: "success",
        title: dataUpdateRuta?.msg,
        confirmButtonText:
          started && !ended ? "Ver puntos de entrega" : undefined,
        showConfirmButton: started && !ended,
        timer: ended ? 2500 : undefined,
      }).then(() => {
        if (started && !ended) {
          navigate(`/ruta/${rutaSeleccionada?.id}`);
        }
      });
    }
    if (error) {
      Swal.fire({
        icon: "error",
        title: errorMessage,
      });
    }
  }, [
    dataUpdateRuta,
    loadingUpdate,
    error,
    errorMessage,
    rutaSeleccionada,
    dispatch,
    navigate,
  ]);

  return (
    <div>
      {/* <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <Dropdown>
          <Dropdown.Toggle variant="danger">
            {filtro === "7" ? "Última semana" : "Último mes"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setFiltro("7")}>
              Última semana
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setFiltro("30")}>
              Último mes
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div> */}

      {error && <Alert variant={"danger"} />}

      {loading && (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner />
        </div>
      )}

      {!loading && !error && rutasLocal && rutasLocal.length > 0 && (
        <div ref={rutasContainerRef}>
          <AnimatePresence>
            {rutasLocal.map((ruta, i) => {
              const {
                id,
                truck,
                cantGuias,
                cantNoEntregadas,
                cantEntregadas,
                started,
                started_at,
                ended,
                ended_at,
              } = ruta;
              const total = cantGuias;

              const percentEntregado =
                parseInt(total) > 0
                  ? Math.round(
                      (parseInt(cantEntregadas) / parseInt(total)) * 100
                    )
                  : 0;
              const percentNoEntregado =
                parseInt(total) > 0
                  ? Math.round(
                      (parseInt(cantNoEntregadas) / (parseInt(total) || 1)) *
                        100
                    )
                  : 0;
              const percentPendientes =
                parseInt(total) > 0
                  ? Math.round(
                      ((parseInt(total) -
                        (parseInt(cantEntregadas) +
                          parseInt(cantNoEntregadas))) /
                        (parseInt(total) || 1)) *
                        100
                    )
                  : 0;
              const bgColor = ended
                ? "bg-finalizada"
                : started
                ? "bg-no-finalizada"
                : "";
              return (
                <div key={id}>
                  <Card
                    className={`card-ruta ${bgColor}`}
                    style={{ cursor: "pointer" }}
                  >
                    <Card.Body onClick={() => onRedirectToPedidos(ruta)}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Card.Title className="mb-1">
                          <Badge bg="secondary" className="me-2">
                            #{truck}
                          </Badge>
                        </Card.Title>
                        <Badge
                          bg={
                            ended ? "success" : started ? "info" : "secondary"
                          }
                        >
                          {ended
                            ? "Finalizada"
                            : started
                            ? "Iniciada"
                            : "No iniciada"}
                        </Badge>
                      </div>
                      <Row>
                        <Col xs={6}>
                          <Card.Text>
                            {moment(ruta.date)
                              .locale("es")
                              .format("ddd, D MMM YYYY")
                              .replace(/^./, (c) => c.toUpperCase())}
                          </Card.Text>
                        </Col>
                        <Col xs={6} className="text-right">
                          <Card.Text>Total: {total}</Card.Text>
                        </Col>
                      </Row>
                      <Card.Text>
                        Iniciada:{" "}
                        {started_at
                          ? moment(started_at)
                              .locale("es")
                              .format("ddd, D MMM YYYY HH:mm")
                              .replace(/^./, (c) => c.toUpperCase())
                          : ""}
                        <br />
                        Finalizada:{" "}
                        {ended_at
                          ? moment(ended_at)
                              .locale("es")
                              .format("ddd, D MMM YYYY HH:mm")
                              .replace(/^./, (c) => c.toUpperCase())
                          : ""}
                      </Card.Text>
                      <ProgressBar className="mb-2" style={{ height: "20px" }}>
                        <ProgressBar
                          variant="success"
                          now={percentEntregado}
                          label={`✔ ${percentEntregado}%`}
                        />
                        <ProgressBar
                          variant="danger"
                          now={percentNoEntregado}
                          label={`❌ ${percentNoEntregado}%`}
                        />
                        {percentEntregado > 0 && (
                          <ProgressBar
                            variant="warning"
                            now={percentPendientes}
                            label={`⌛ ${percentPendientes}%`}
                          />
                        )}
                      </ProgressBar>
                    </Card.Body>
                    <Card.Footer
                      style={{
                        backgroundColor: "transparent",
                        borderTop: "none",
                      }}
                    >
                      <div className="d-flex justify-content-center mb-2">
                        {!started && !ended && (
                          <>
                            {loadingUpdate && id === rutaSeleccionada?.id && (
                              <Button
                                className="btn-accion btn-entregado"
                                size="sm"
                                disabled={loadingUpdate}
                              >
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Iniciando Ruta...
                                </>
                              </Button>
                            )}
                            {!loadingUpdate && !started && (
                              <Button
                                className="btn-accion btn-entregado"
                                size="sm"
                                onClick={() => onAccionClick(ruta, "iniciar")}
                              >
                                Iniciar
                              </Button>
                            )}
                          </>
                        )}
                        {started && !ended && (
                          <>
                            {loadingUpdate && id === rutaSeleccionada?.id && (
                              <Button
                                className="btn-accion btn-noentregado"
                                size="sm"
                                disabled={loadingUpdate}
                              >
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Finalizando Ruta...
                                </>
                              </Button>
                            )}
                            {!loadingUpdate && !ended && (
                              <>
                                <Button
                                  className="btn-accion btn-continuar"
                                  size="sm"
                                  onClick={() => onRedirectToPedidos(ruta)}
                                >
                                  Continuar
                                </Button>
                                <Button
                                  className="btn-accion btn-noentregado"
                                  size="sm"
                                  onClick={() =>
                                    onAccionClick(ruta, "finalizar")
                                  }
                                >
                                  Finalizar
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </Card.Footer>
                  </Card>
                </div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {!loading && !error && rutasLocal && rutasLocal.length === 0 && (
        <Card className="mt-5">
          <Card.Body>
            <Card.Text className="text-center mt-2">
              No se encontraron rutas
            </Card.Text>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ListaRutas;
